import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import Divider from "components/divider";
import FormLayout from "components/form/FormLayout";
import Toggle from "components/form/toggle/Toggle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import SelectNew from "components/select/SelectNew";
import { TCustomerType } from "redux/reducers/models/facility";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { StatusCode } from "api/protocols";
import axios, { CancelToken } from "axios";
import { showError } from "redux/actions/ui";
import useDebounce from "hooks/events/useDebounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import "./facilitySettings.scss";

interface IProps {
  state: Partial<IPutTeeSheetSettings>;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

export default function FacilityRegisterSettings(props: IProps) {
  const { state, updateState } = props;
  const { t } = useTranslation();
  const { Option } = Select;
  const { facilityStore } = useAppSelector(store => store);
  const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>(undefined);
  const [customerTypeSearch, setCustomerTypeSearch] = useState("");
  const debouncedSearch = useDebounce(customerTypeSearch, 500);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomerTypes(source.token);
    return () => source.cancel();
  }, [debouncedSearch]);

  async function loadCustomerTypes(token?: CancelToken) {
    const typeRes = await GetCustomerType(
      {
        search: debouncedSearch,
        application: "green_fee",
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (typeRes.status !== StatusCode.OK) {
      dispatch(showError(typeRes.message ?? "Error loading customer types"));
      setCustomerTypes([]);
      return;
    }

    setCustomerTypes(typeRes.data);
  }

  function handleSelectCustomerType(id: number) {
    let updatedCustomerTypes = state?.review_players_modal_customer_types
      ? [...state.review_players_modal_customer_types]
      : [];
    const foundType = updatedCustomerTypes?.find(typeId => typeId === id);
    if (foundType) {
      updatedCustomerTypes = updatedCustomerTypes?.filter(typeId => typeId !== id);
    } else {
      updatedCustomerTypes.push(id);
    }
    updateState("review_players_modal_customer_types", updatedCustomerTypes);
  }

  return (
    <div className="facility-settings-section p-2">
      <p className="facility-settings-subsection-title">{t("Register controls")}</p>
      <div className="facility-settings-toggle-stack">
        <Toggle
          labelRight={t("Void note required")}
          labelSupport={t("Note is required when voiding an order.")}
          onChange={e => updateState("orders_void_with_note", e.target.checked)}
          checked={state?.orders_void_with_note}
        />
        <Toggle
          labelRight={t("Blind close")}
          labelSupport={t("Closes the register without showing totals.")}
          onChange={e => updateState("register_blind_close", e.target.checked)}
          checked={state?.register_blind_close}
        />
        <Toggle
          labelRight={t("Round change due")}
          labelSupport={t(
            "When enabled, cash change due is rounded to the nearest $0.05 for register and table service payments.",
          )}
          onChange={e => updateState("register_cash_round_change", e.target.checked)}
          checked={state?.register_cash_round_change}
        />
        <Toggle
          labelRight={t("Stack line items")}
          labelSupport={t("Groups matching items together for easier review.")}
          onChange={e => updateState("line_items_stack", e.target.checked)}
          checked={state?.line_items_stack}
        />
        <Toggle
          labelRight={t("Show outstanding balance")}
          labelSupport={t(
            "When enabled, a popup appears when adding a customer to the cart if they have an outstanding balance.",
          )}
          onChange={e => updateState("register_show_outstanding_balance", e.target.checked)}
          checked={state?.register_show_outstanding_balance}
        />
      </div>

      <Divider />

      <p className="facility-settings-subsection-title">{t("Display options")}</p>
      <div className="facility-settings-toggle-stack">
        <Toggle
          labelRight={t("Signature line on receipt")}
          onChange={e => updateState("receipts_show_signature", e.target.checked)}
          checked={state?.receipts_show_signature}
        />
        {state?.receipts_show_signature ? (
          <Toggle
            labelRight={t("Receipt name")}
            onChange={e => updateState("receipts_show_name", e.target.checked)}
            checked={state?.receipts_show_name}
          />
        ) : null}
        {state?.receipts_show_signature ? (
          <Toggle
            labelRight={t("Receipt member number")}
            onChange={e => updateState("receipts_show_member_number", e.target.checked)}
            checked={state?.receipts_show_member_number}
          />
        ) : null}
        <Toggle
          labelRight={t("Vendor on product card")}
          onChange={e => updateState("product_card_display_vendor", e.target.checked)}
          checked={state?.product_card_display_vendor}
        />
        <Toggle
          labelRight={t("Inventory on product card")}
          onChange={e => updateState("product_card_display_inventory", e.target.checked)}
          checked={state?.product_card_display_inventory}
        />
        <Toggle
          labelRight={t("Product images on product card")}
          onChange={e => updateState("show_product_images", e.target.checked)}
          checked={state?.show_product_images}
        />
      </div>

      <Divider />

      <FormLayout>
        <FormLayout.Group>
          <Select
            label={t("Default POS view")}
            defaultValue={state?.pos_app_default_view}
            onChange={(value: "quick_sale" | "table_service") => updateState("pos_app_default_view", value)}
            helpText={t("Choose the screen POS opens to by default.")}
          >
            <Option value="quick_sale">{t("Quick Sale")}</Option>
            <Option value="table_service">{t("Table Service")}</Option>
          </Select>
        </FormLayout.Group>
      </FormLayout>

      {facilityStore?.facility?.display_review_players_modal ? (
        <>
          <Divider />
          <FormLayout>
            <FormLayout.Group>
              <SelectNew
                label={t("Review Player Details Modal Customer Types")}
                id={"new_restriction_customer_type_select"}
                options={customerTypes?.map(type => ({
                  id: type.id,
                  label: `${type.title}${type.subtitle ? " - " : ""}${type.subtitle ? type.subtitle : ""}`,
                  extraValue: type,
                }))}
                onChange={value => handleSelectCustomerType(value)}
                multiValue={state?.review_players_modal_customer_types ?? []}
                onSearchCallback={(query: string) => setCustomerTypeSearch(query)}
                searchable
                showAmountSelected
                tooltip={t(
                  "Automatically display the review player details modal on the tee sheet for customers of the selected customer types",
                )}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <div className="flex gap-2 items-center flex-wrap">
                {customerTypes
                  ?.filter(type => state?.review_players_modal_customer_types?.includes(type?.id))
                  ?.map((type, index) => {
                    return (
                      <div key={index} className="facility-register-settings-tag">
                        <p className="facility-register-settings-tag-text">{type?.full_title}</p>
                        <FontAwesomeIcon
                          icon={faXmark}
                          className="facility-register-settings-tag-x"
                          onClick={() => handleSelectCustomerType(type?.id)}
                        />
                      </div>
                    );
                  })}
              </div>
            </FormLayout.Group>
          </FormLayout>
        </>
      ) : null}
    </div>
  );
}
